import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { cartApi, checkoutApi, cartTokenStorage, authTokenStorage } from '@/lib/api'
import { Link } from '@tanstack/react-router'

interface CartItem {
  id: number
  product_id: number
  product_name: string
  quantity: number
  price: number
  image_url?: string
}

interface Cart {
  items: CartItem[]
  subtotal: number
  total: number
  cart_token?: string
}

interface ShippingAddress {
  first_name: string
  last_name: string
  email: string
  phone: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  postal_code: string
  country: string
}

export const Route = createFileRoute('/checkout')({
  component: CheckoutPage,
})

function CheckoutPage() {
  const _navigate = useNavigate()
  void _navigate // Reserved for future use
  const [cart, setCart] = useState<Cart | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<'shipping' | 'payment' | 'confirmation'>('shipping')
  const [discountCode, setDiscountCode] = useState('')
  const [discountApplied, setDiscountApplied] = useState(false)
  const [error, setError] = useState('')

  const [shipping, setShipping] = useState<ShippingAddress>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
  })

  const currencySymbol = '₹'

  useEffect(() => {
    loadCart()
  }, [])

  const loadCart = async () => {
    try {
      const data = await cartApi.get() as Cart
      setCart(data)

      // Store cart token if returned
      if (data.cart_token) {
        cartTokenStorage.set(data.cart_token)
      }
    } catch (error) {
      console.error('Failed to load cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleShippingChange = (field: keyof ShippingAddress, value: string) => {
    setShipping((prev) => ({ ...prev, [field]: value }))
  }

  const applyDiscount = async () => {
    if (!discountCode) return

    try {
      const cartToken = cartTokenStorage.get()
      if (!cartToken) {
        setError('Cart not found')
        return
      }

      // Apply discount to cart
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/cart/apply-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Cart-Token': cartToken,
        },
        body: JSON.stringify({ code: discountCode }),
      })

      if (response.ok) {
        setDiscountApplied(true)
        await loadCart()
        setError('')
      } else {
        const data = await response.json()
        setError(data.detail || 'Invalid discount code')
      }
    } catch (err) {
      setError('Failed to apply discount')
    }
  }

  const handleSubmitShipping = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Basic validation
    if (!shipping.first_name || !shipping.last_name || !shipping.email || !shipping.phone) {
      setError('Please fill in all required fields')
      return
    }
    if (!shipping.address_line1 || !shipping.city || !shipping.state || !shipping.postal_code) {
      setError('Please fill in your complete address')
      return
    }

    setStep('payment')
  }

  const handlePlaceOrder = async () => {
    setSubmitting(true)
    setError('')

    try {
      const cartToken = cartTokenStorage.get()
      if (!cartToken) {
        setError('Cart not found. Please add items to cart first.')
        setSubmitting(false)
        return
      }

      // Transform frontend address format to backend format
      const shippingAddress = {
        address_type: 'shipping',
        full_name: `${shipping.first_name} ${shipping.last_name}`.trim(),
        email: shipping.email,
        phone: shipping.phone,
        address_line1: shipping.address_line1,
        address_line2: shipping.address_line2 || undefined,
        city: shipping.city,
        state: shipping.state,
        pincode: shipping.postal_code,
        country: shipping.country,
      }

      const billingAddress = {
        address_type: 'billing',
        full_name: `${shipping.first_name} ${shipping.last_name}`.trim(),
        email: shipping.email,
        phone: shipping.phone,
        address_line1: shipping.address_line1,
        address_line2: shipping.address_line2 || undefined,
        city: shipping.city,
        state: shipping.state,
        pincode: shipping.postal_code,
        country: shipping.country,
      }

      // Create order with correct schema
      const orderData = {
        cart_token: cartToken,
        email: shipping.email,
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        payment_method: 'cod',
        discount_code: discountApplied ? discountCode : undefined,
      }

      const order = await checkoutApi.createOrder(orderData) as { id: number; order_number: string }

      // Clear cart token after successful order
      cartTokenStorage.clear()

      // Navigate to confirmation
      setStep('confirmation')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to place order'
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Checkout</h1>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold mb-4">Your Cart is Empty</h1>
        <p className="text-gray-600 mb-8">Add some products to checkout.</p>
        <Link
          to="/products"
          search={{}}
          className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
        >
          Continue Shopping
        </Link>
      </div>
    )
  }

  if (step === 'confirmation') {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-4">Order Placed Successfully!</h1>
          <p className="text-gray-600 mb-8">
            Thank you for your order. We'll send you a confirmation email shortly.
          </p>
          <Link
            to="/products"
            search={{}}
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Checkout</h1>

      {/* Steps indicator */}
      <div className="flex items-center justify-center mb-8">
        <div className={`flex items-center ${step === 'shipping' ? 'text-blue-600' : 'text-gray-400'}`}>
          <span className="w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2">1</span>
          <span>Shipping</span>
        </div>
        <div className="w-16 h-0.5 bg-gray-300 mx-4" />
        <div className={`flex items-center ${step === 'payment' ? 'text-blue-600' : 'text-gray-400'}`}>
          <span className="w-8 h-8 rounded-full border-2 flex items-center justify-center mr-2">2</span>
          <span>Payment</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {step === 'shipping' && (
            <form onSubmit={handleSubmitShipping} className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-bold mb-6">Shipping Information</h2>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    value={shipping.first_name}
                    onChange={(e) => handleShippingChange('first_name', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={shipping.last_name}
                    onChange={(e) => handleShippingChange('last_name', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={shipping.email}
                    onChange={(e) => handleShippingChange('email', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={shipping.phone}
                    onChange={(e) => handleShippingChange('phone', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Address Line 1 *</label>
                <input
                  type="text"
                  value={shipping.address_line1}
                  onChange={(e) => handleShippingChange('address_line1', e.target.value)}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Street address"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={shipping.address_line2}
                  onChange={(e) => handleShippingChange('address_line2', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Apartment, suite, etc."
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input
                    type="text"
                    value={shipping.city}
                    onChange={(e) => handleShippingChange('city', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <input
                    type="text"
                    value={shipping.state}
                    onChange={(e) => handleShippingChange('state', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-1">Postal Code *</label>
                  <input
                    type="text"
                    value={shipping.postal_code}
                    onChange={(e) => handleShippingChange('postal_code', e.target.value)}
                    required
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input
                    type="text"
                    value={shipping.country}
                    onChange={(e) => handleShippingChange('country', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
                    readOnly
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Continue to Payment
              </button>
            </form>
          )}

          {step === 'payment' && (
            <div className="bg-white rounded-lg border p-6">
              <h2 className="text-xl font-bold mb-6">Payment Method</h2>

              <div className="mb-6">
                <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="payment" defaultChecked className="mr-3" />
                  <div>
                    <div className="font-medium">Cash on Delivery</div>
                    <div className="text-sm text-gray-500">Pay when you receive your order</div>
                  </div>
                </label>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setStep('shipping')}
                  className="flex-1 py-3 border rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePlaceOrder}
                  disabled={submitting}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div>
          <div className="bg-white rounded-lg border p-6 sticky top-24">
            <h2 className="font-bold text-lg mb-4">Order Summary</h2>

            <div className="divide-y mb-4">
              {cart.items.map((item) => (
                <div key={item.id} className="py-3 flex gap-3">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.product_name} className="w-16 h-16 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded" />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-medium">{item.product_name}</div>
                    <div className="text-sm text-gray-500">Qty: {item.quantity}</div>
                  </div>
                  <div className="text-sm font-medium">
                    {currencySymbol}{(item.price * item.quantity).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {/* Discount Code */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Discount Code</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  disabled={discountApplied}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100"
                  placeholder="Enter code"
                />
                <button
                  onClick={applyDiscount}
                  disabled={discountApplied || !discountCode}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {discountApplied ? 'Applied' : 'Apply'}
                </button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>{currencySymbol}{cart.subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="text-gray-600">Calculated at checkout</span>
              </div>
              {discountApplied && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>- Applied</span>
                </div>
              )}
            </div>

            <div className="border-t mt-4 pt-4">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>{currencySymbol}{cart.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
